import { AdvertisedPromotion, PromotionIntent } from '../../../../types/cart'

type IntentControls = {
  setCode: (value: string) => void
  selectOffer: (offer: AdvertisedPromotion) => void
  /** Drop the applied code and fall back to automatic. Backs the applied row's Remove. */
  clear: () => void
}

/**
 * The three ways a booker changes what they are asking for.
 *
 * They no longer clear each other, and the removal of that rule is the reason this hook is
 * worth reading. An earlier version said the two intent fields are "MUTUALLY EXCLUSIVE in
 * PromotionIntentInput" and that sending both is "an intent the service rejects". Both
 * claims were false:
 *
 *   - the schema declares two independent fields
 *     (`PromotionIntentInput { applyAutomatic: Boolean! = false, code: String }`)
 *   - loyalty's `resolveIntent` errors only when NEITHER is set
 *   - `handleStepUp` in BookingStepper already sends BOTH whenever a code is present
 *
 * The exclusion also did not do what it implied. `AdvertisementAllowed` gates the advertised
 * list to `code_entry` promotions, while `applyAutomatic` applies only `auto_applied` ones —
 * two DISJOINT sets. So clearing one to set the other never chose between competing routes
 * to one discount; it forfeited one of two independent discounts. Reported twice: "nothing is
 * applied" and "the Apply button does not work".
 *
 * `applyAutomatic` is therefore pinned true and is no longer a user-facing control at all —
 * see PropertyDiscounts for why an auto-applied promotion is status rather than a choice.
 */
export function usePromotionIntent(
  intent: PromotionIntent,
  setIntent: React.Dispatch<React.SetStateAction<PromotionIntent>>
): IntentControls {
  return {
    // applyAutomatic stays TRUE regardless of the code. The two are not alternatives: the
    // advertised list is gated to code_entry promotions and applyAutomatic applies only
    // auto_applied ones, so turning one off to set the other forfeits a discount the
    // property already granted. The server has always accepted both together —
    // resolveIntent errors only when NEITHER is set.
    setCode: (value: string) => {
      const code = value.trim()
      setIntent({ applyAutomatic: true, code: code === '' ? undefined : code })
    },

    // Not setCode('') — Remove is a decision, an empty field is an edit in progress, and
    // the two differ in intent even where they agree in effect today.
    clear: () => {
      setIntent({ applyAutomatic: true, code: undefined })
    },

    // Picking an offer IS entering its code — the advertised surface only carries
    // shared-code offers, so there is nothing else to select.
    //
    // No longer toggles off on re-selection. That gesture was the only route back to "no
    // code", and radios have no deselect semantics: it could not be reached with arrow
    // keys and nothing announced it existed. The group now carries an explicit
    // "No code discount" option, which calls clear().
    selectOffer: (offer: AdvertisedPromotion) => {
      setIntent({ applyAutomatic: true, code: offer.code })
    }
  }
}
